import React, { lazy, Suspense } from 'react';
import { Toaster } from 'react-hot-toast';
import { Routes, Route, Navigate } from 'react-router-dom';
import { MobileAuthGuard } from './components/mobile/layout/MobileAuthGuard';

const MobileLayout = lazy(() => import('./components/mobile/layout/MobileLayout').then(m => ({ default: m.MobileLayout })));
const MobileLogin = lazy(() => import('./components/mobile/screens/auth/MobileLogin').then(m => ({ default: m.MobileLogin })));
const MobileVerify = lazy(() => import('./components/mobile/screens/auth/MobileVerify').then(m => ({ default: m.MobileVerify })));
const MobileAuthCallback = lazy(() => import('./components/mobile/screens/auth/MobileAuthCallback').then(m => ({ default: m.MobileAuthCallback })));
const HomeScreen = lazy(() => import('./components/mobile/screens/home/HomeScreen').then(m => ({ default: m.HomeScreen })));
const LibraryScreen = lazy(() => import('./components/mobile/screens/library/LibraryScreen').then(m => ({ default: m.LibraryScreen })));
const AlbumDetail = lazy(() => import('./components/mobile/screens/library/AlbumDetail').then(m => ({ default: m.AlbumDetail })));
const AlbumCreate = lazy(() => import('./components/mobile/screens/library/AlbumCreate').then(m => ({ default: m.AlbumCreate })));
const AlbumEdit = lazy(() => import('./components/mobile/screens/library/AlbumEdit').then(m => ({ default: m.AlbumEdit })));
const FolderDetail = lazy(() => import('./components/mobile/screens/library/FolderDetail').then(m => ({ default: m.FolderDetail })));
const FolderCreate = lazy(() => import('./components/mobile/screens/library/FolderCreate').then(m => ({ default: m.FolderCreate })));
const DocumentView = lazy(() => import('./components/mobile/screens/library/DocumentView').then(m => ({ default: m.DocumentView })));
const DocumentCreate = lazy(() => import('./components/mobile/screens/library/DocumentCreate').then(m => ({ default: m.DocumentCreate })));
const SpreadsheetView = lazy(() => import('./components/mobile/screens/library/SpreadsheetView').then(m => ({ default: m.SpreadsheetView })));
const SpreadsheetCreate = lazy(() => import('./components/mobile/screens/library/SpreadsheetCreate').then(m => ({ default: m.SpreadsheetCreate })));
const AssetViewer = lazy(() => import('./components/mobile/screens/asset/AssetViewer').then(m => ({ default: m.AssetViewer })));
const ImportHub = lazy(() => import('./components/mobile/screens/import/ImportHub').then(m => ({ default: m.ImportHub })));
const FacebookImportScreen = lazy(() => import('./components/mobile/screens/import/FacebookImportScreen').then(m => ({ default: m.FacebookImportScreen })));
const GoogleImportScreen = lazy(() => import('./components/mobile/screens/import/GoogleImportScreen').then(m => ({ default: m.GoogleImportScreen })));
const SearchScreen = lazy(() => import('./components/mobile/screens/search/SearchScreen').then(m => ({ default: m.SearchScreen })));
const SettingsScreen = lazy(() => import('./components/mobile/screens/settings/SettingsScreen').then(m => ({ default: m.SettingsScreen })));
const AccountScreen = lazy(() => import('./components/mobile/screens/settings/AccountScreen').then(m => ({ default: m.AccountScreen })));
const PinchTest = lazy(() => import('./components/mobile/screens/PinchTest').then(m => ({ default: m.PinchTest })));
const BillingScreen = lazy(() => import('./components/mobile/screens/settings/BillingScreen').then(m => ({ default: m.BillingScreen })));
const DevicesScreen = lazy(() => import('./components/mobile/screens/settings/DevicesScreen').then(m => ({ default: m.DevicesScreen })));
const DataExportScreen = lazy(() => import('./components/mobile/screens/settings/DataExportScreen').then(m => ({ default: m.DataExportScreen })));
const NotificationsScreen = lazy(() => import('./components/mobile/screens/settings/NotificationsScreen').then(m => ({ default: m.NotificationsScreen })));
const ChatListScreen = lazy(() => import('./components/mobile/screens/chat/ChatListScreen').then(m => ({ default: m.ChatListScreen })));
const ChatConversationScreen = lazy(() => import('./components/mobile/screens/chat/ChatConversationScreen').then(m => ({ default: m.ChatConversationScreen })));
import { AlbumViewer, AlbumDetailView } from './components/AlbumViewer';
import { ViewCircle } from './components/ViewCircle';
import { AdminDashboard } from './components/AdminDashboard';
import { Login } from './components/Login';
import { Signup } from './components/Signup';
import { SignupSuccess } from './components/SignupSuccess';
import { AuthCallback } from './components/AuthCallback';
import { VerifyRegistration } from './components/VerifyRegistration';
import { RootRedirect } from './components/RootRedirect';
import { useMobileRedirect } from './components/hooks/useMobileRedirect';
import { Dashboard } from './components/Dashboard';
import { FrameSetup } from './components/FrameSetup';
import { JoinCircle } from './components/JoinCircle';
import { StartCircle } from './components/StartCircle';
import { FrameMode } from './components/FrameMode';
import { ShareRequest } from './components/ShareRequest';
import { SharedAssetViewer } from './components/SharedAssetViewer';
import { SharedAlbumViewer } from './components/SharedAlbumViewer';
import { SubscriptionPage } from './components/SubscriptionPage';
import { AccountPage } from './components/AccountPage';
import { FacebookImport } from './components/FacebookImport';
import { FacebookFriendsInvite } from './components/FacebookFriendsInvite';
import { Unsubscribe } from './components/Unsubscribe';
import { SecurityNotice } from './components/SecurityNotice';
import { EmailReply } from './components/EmailReply';
import { NotificationsPage } from './components/NotificationsPage';
import { AlbumInvite } from './components/AlbumInvite';
import { InvitePage } from './components/InvitePage';
import { ConnectInvite } from './components/ConnectInvite';
import { TokenInvite } from './components/TokenInvite';

const EventHub = lazy(() => import('./components/events/EventHub'));
const EventScreenLazy = lazy(() => import('./components/mobile/screens/event/EventScreen').then(m => ({ default: m.EventScreen })));
const EventGuestJoinLazy = lazy(() => import('./components/mobile/screens/event/EventGuestJoin').then(m => ({ default: m.EventGuestJoin })));
const EventLive = lazy(() => import('./components/events/EventLive'));
const EventWall = lazy(() => import('./components/events/EventWall'));
const WallPreview = lazy(() => import('./components/events/WallPreview'));
const EventUpload = lazy(() => import('./components/events/EventUpload'));
const EventClosing = lazy(() => import('./components/events/EventClosing'));
const ApproveRequest = lazy(() => import('./components/events/ApproveRequest'));

// Redirect to mobile app when on mobile domain
function MobileDomainRedirect({ fallback }: { fallback: React.ReactNode }) {
  const isMobileDomain = window.location.hostname.includes('mobile.kizu.online');
  if (isMobileDomain) {
    return <Navigate to="/mobile/home" replace />;
  }
  return <>{fallback}</>;
}

function App() {
  useMobileRedirect();

  return (
    <div className="App">
      <Toaster position="top-right" />
      <Routes>
        {/* Public route for album viewing without authentication */}
        <Route path="/album/:inviteId" element={<AlbumViewer />} />
        <Route path="/album/:inviteId/:albumId" element={<AlbumDetailView />} />

        {/* Circle invitation acceptance route */}
        <Route path="/view-circle/:inviteId" element={<ViewCircle />} />

        {/* QR Album Invite route */}
        <Route path="/album-invite/:token" element={<AlbumInvite />} />

        {/* QR Circle Invite route */}
        {/* /join-circle/:token is rewritten by vercel.json to /api/event-invite for OG tags,
            which then 302-redirects real browsers here. */}
        <Route path="/join-circle/:token" element={<JoinCircle />} />
        <Route path="/join-circle-web/:token" element={<JoinCircle />} />

        {/* Circle Seed route */}
        <Route path="/start-circle/:token" element={<StartCircle />} />

        {/* Picture Frame Setup route */}
        <Route path="/frame-setup/:token" element={<FrameSetup />} />

        {/* Picture Frame Display Mode */}
        <Route path="/frame" element={<FrameMode />} />

        {/* Share request route */}
        <Route path="/share" element={<ShareRequest />} />

        {/* Shared asset viewer route */}
        <Route path="/asset/:token" element={<SharedAssetViewer />} />

        {/* Shared album viewer route */}
        <Route path="/shared-album/:token" element={<SharedAlbumViewer />} />

        {/* Invite landing pages */}
        <Route path="/invite/:token" element={<InvitePage />} />
        <Route path="/connect/:uid" element={<ConnectInvite />} />
        <Route path="/i/:token" element={<TokenInvite />} />
        <Route path="/j/:uid" element={<ConnectInvite />} />

        {/* Live Wall event routes */}
        <Route path="/events/:eventId" element={<Suspense fallback={<div />}><EventHub /></Suspense>} />
        <Route path="/events/:eventId/live" element={<Suspense fallback={<div />}><EventLive /></Suspense>} />
        <Route path="/events/:eventId/wall" element={<Suspense fallback={<div />}><EventWall /></Suspense>} />
        <Route path="/events/:eventId/wall/:subEventId" element={<Suspense fallback={<div />}><WallPreview /></Suspense>} />
        <Route path="/events/:eventId/upload" element={<Suspense fallback={<div />}><EventUpload /></Suspense>} />
        <Route path="/events/:eventId/closing" element={<Suspense fallback={<div />}><EventClosing /></Suspense>} />
        <Route path="/approve-request/:token" element={<Suspense fallback={<div />}><ApproveRequest /></Suspense>} />

        {/* Email unsubscribe (public, no auth) */}
        <Route path="/unsubscribe" element={<Unsubscribe />} />

        {/* Security notice landing page (public, no auth) — destination of
            Supabase edge function redirects since edge functions can't serve
            HTML directly (gateway forces text/plain + sandbox CSP). */}
        <Route path="/security-notice" element={<SecurityNotice />} />

        {/* Email reply landing page — /reply/:token from notification email Reply button */}
        <Route path="/reply/:token" element={<EmailReply />} />

        {/* Subscription management route */}
        <Route path="/subscription" element={<SubscriptionPage />} />

        {/* Account settings and deletion route */}
        <Route path="/account" element={<AccountPage />} />

        {/* Registration verification route */}
        <Route path="/verify" element={<VerifyRegistration />} />

        {/* Facebook import routes */}
        <Route path="/import/facebook" element={<FacebookImport />} />
        <Route path="/import/facebook/invite" element={<FacebookFriendsInvite />} />

        {/* User dashboard (for circle members) */}
        <Route path="/dashboard" element={<Dashboard />} />

        {/* Notifications page (linked from email digests) */}
        <Route path="/notifications" element={<NotificationsPage />} />

        {/* Authentication routes */}
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/signup-success" element={<SignupSuccess />} />
        <Route path="/auth/callback" element={<AuthCallback />} />

        {/* Admin routes (require authentication) */}
        <Route path="/admin/*" element={<AdminDashboard />} />

        {/* Mobile Web App */}
        <Route path="/mobile" element={
          <Suspense fallback={<div className="flex items-center justify-center h-screen bg-[#0f172a]"><div className="animate-spin w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full" /></div>}>
            <MobileAuthGuard>
              <MobileLayout />
            </MobileAuthGuard>
          </Suspense>
        }>
          <Route index element={<Navigate to="/mobile/home" replace />} />
          <Route path="home" element={<HomeScreen />} />
          <Route path="library" element={<LibraryScreen />} />
          <Route path="album/create" element={<AlbumCreate />} />
          <Route path="album/:albumId" element={<AlbumDetail />} />
          <Route path="album/:albumId/edit" element={<AlbumEdit />} />
          <Route path="folder/create" element={<FolderCreate />} />
          <Route path="folder/:folderId" element={<FolderDetail />} />
          <Route path="document/create" element={<DocumentCreate />} />
          <Route path="document/:documentId" element={<DocumentView />} />
          <Route path="spreadsheet/create" element={<SpreadsheetCreate />} />
          <Route path="spreadsheet/:listId" element={<SpreadsheetView />} />
          <Route path="search" element={<SearchScreen />} />
          <Route path="asset/:assetId" element={<AssetViewer />} />
          <Route path="import" element={<ImportHub />} />
          <Route path="import/facebook" element={<FacebookImportScreen />} />
          <Route path="import/google" element={<GoogleImportScreen />} />
          <Route path="settings" element={<SettingsScreen />} />
          <Route path="account" element={<AccountScreen />} />
          <Route path="pinch-test" element={<PinchTest />} />
          <Route path="billing" element={<BillingScreen />} />
          <Route path="devices" element={<DevicesScreen />} />
          <Route path="export" element={<DataExportScreen />} />
          <Route path="notifications" element={<NotificationsScreen />} />
          <Route path="chat" element={<ChatListScreen />} />
        </Route>

        {/* Event guest join — NO auth guard (QR scan landing page) */}
        <Route path="/mobile/event/:eventId/join" element={
          <Suspense fallback={<div className="flex items-center justify-center h-screen bg-[#0f172a]"><div className="animate-spin w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full" /></div>}>
            <EventGuestJoinLazy />
          </Suspense>
        } />

        {/* Event mode (full-screen, own tab bar) */}
        <Route path="/mobile/event/:eventId" element={
          <Suspense fallback={<div className="flex items-center justify-center h-screen bg-[#0f172a]"><div className="animate-spin w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full" /></div>}>
            <MobileAuthGuard>
              <EventScreenLazy />
            </MobileAuthGuard>
          </Suspense>
        } />

        {/* Chat conversation (full-screen, no tab bar) */}
        <Route path="/mobile/chat/:conversationId" element={
          <Suspense fallback={<div className="flex items-center justify-center h-screen bg-[#0f172a]"><div className="animate-spin w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full" /></div>}>
            <MobileAuthGuard>
              <ChatConversationScreen />
            </MobileAuthGuard>
          </Suspense>
        } />

        {/* Mobile Auth (no auth guard) */}
        <Route path="/mobile/auth/login" element={
          <Suspense fallback={<div className="flex items-center justify-center h-screen bg-[#0f172a]"><div className="animate-spin w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full" /></div>}>
            <MobileLogin />
          </Suspense>
        } />
        <Route path="/mobile/auth/verify" element={
          <Suspense fallback={<div className="flex items-center justify-center h-screen bg-[#0f172a]"><div className="animate-spin w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full" /></div>}>
            <MobileVerify />
          </Suspense>
        } />
        <Route path="/mobile/auth/callback" element={
          <Suspense fallback={<div className="flex items-center justify-center h-screen bg-[#0f172a]"><div className="animate-spin w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full" /></div>}>
            <MobileAuthCallback />
          </Suspense>
        } />

        {/* Mobile domain: redirect root and unmatched paths to /mobile/home */}
        <Route path="/" element={<MobileDomainRedirect fallback={<RootRedirect />} />} />
        <Route path="*" element={<MobileDomainRedirect fallback={<RootRedirect />} />} />
      </Routes>
    </div>
  );
}

export default App;
